/**
 * Cursor-based pagination (spec §13.1).
 *
 * Keyset, not offset. Offset degrades badly on the tables that matter most
 * here: an item grid over 1M SKUs, or an invoice history over 10M lines, would
 * both re-scan and skip rows under concurrent writes. A cursor keyed on a unique
 * monotonic column keeps page 500 as cheap as page 1.
 *
 * The cursor itself is opaque and stays server-side. The client echoes
 * `nextCursor` back verbatim and must never construct or parse one, so the
 * encoding can change without breaking offline terminals holding cursors in a
 * local queue.
 */

import { z } from 'zod';

export const DEFAULT_PAGE_SIZE = 25;
export const MAX_PAGE_SIZE = 200;

export const paginationQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(DEFAULT_PAGE_SIZE),
  cursor: z.string().optional(),
  sort: z.string().optional(),
});

export type PaginationQuery = z.infer<typeof paginationQuerySchema>;

export interface PageInfo {
  hasNextPage: boolean;
  nextCursor: string | null;
  /** Present only when the caller asked for it; a COUNT is O(n) on large tables. */
  totalCount?: number;
}

export interface Page<T> {
  data: T[];
  pageInfo: PageInfo;
}

/** True when the server said there is more, regardless of how full this page is. */
export function hasMore<T>(page: Page<T>): boolean {
  return page.pageInfo.hasNextPage && page.pageInfo.nextCursor !== null;
}
